import { SentryError } from '@ifixit/sentry';
import { _js } from '@ifixit/utils/localize';
import { toast } from '@ifixit/ui/components/toast';
import { callAjaxIO } from 'Shared/ajax_io';
import { beginModalPending } from 'Shared/editor/shortcut_scope';

/**
 * Launchers for the legacy MooTools image dialogs — markers and crop — plus
 * the copy-to-library action, callable from the React editors.
 *
 * The dialogs themselves stay MooTools: they're canvas-heavy (fabric.js,
 * cropperjs), shared with every legacy page, and load on demand. What this
 * module supplies is the page wiring a legacy page gets from media_manager.js:
 * the Modal event handlers that boot each dialog, and a MediaItem wrapper
 * around a bare image record.
 *
 * The host page's PHP must register the dialogs' AjaxIO handlers —
 * loadModalAjaxModule, editImageMarkers, cropImage, cropImageStatus and
 * copyToLibrary (see GuideEditLib::displayV2) — since AjaxIO posts back to
 * the page's own URL.
 */

export type LegacyImageRenditions = {
   thumbnail?: string | null;
   standard?: string | null;
   medium?: string | null;
   original?: string | null;
};

export type LegacyImageInput = { imageid: number } & LegacyImageRenditions;

/**
 * One line of the guide step an image sits on: its bullet name (red,
 * light_blue, icon_note...) and its text. The markers dialog lists the
 * coloured ones so markers can be matched to them.
 */
export type StepBullet = { bullet: string; text: string };

/** The replacement image a saved markers/crop edit produced. */
export type LegacyEditedImage = {
   imageid: number;
   thumbnail: string | null;
   standard: string | null;
   medium: string | null;
   original: string | null;
};

/** The slice of the MooTools MediaItem these launchers touch. */
type LegacyMediaItem = {
   getID: () => number;
   /** `notReady` fires synchronously when a write is still in flight. */
   whenReady: (ready: () => void, notReady?: () => void) => void;
   /** MediaItemData accessors: `data.imageid()` reads, `data.imageid(1)` writes. */
   data: Record<string, (value?: unknown) => unknown>;
};

type ImageMarkersDialog = {
   initialize: (contentBox: unknown, options: unknown, responseData: unknown) => void;
   unload: () => void;
};

type ImageCropDialog = {
   showForMediaItem: (
      mediaItem: LegacyMediaItem,
      mediaTarget: { getMediaFilterName: () => string },
      callback: (cropped: LegacyMediaItem | null | undefined) => void
   ) => void;
};

/**
 * The MooTools Modal as these launchers use it. Its events surface comes
 * from an Object.append(Modal, EventsFunctions) TS can't see through, so
 * the import gets widened to this shape.
 */
type LegacyModal = {
   open: (options: Record<string, unknown>) => void;
   addEvents: (events: Record<string, (...args: never[]) => void>) => void;
   addEvent: (type: string, fn: (clientOptions: unknown) => void) => void;
   removeEvent: (type: string, fn: (clientOptions: unknown) => void) => void;
   /** EventsFunctions storage; keys are event names sans "on" (camelCase). */
   $events?: Record<string, unknown[]>;
};

async function loadModal(): Promise<LegacyModal> {
   const { Modal } = await import('Shared/modal');
   return Modal as unknown as LegacyModal;
}

/**
 * Rejects when the modal reports that `module` didn't load for the open
 * whose clientOptions `isThisOpen` recognizes. The modal has closed itself
 * by then; this is how the caller finds out.
 */
function watchLoadError(
   Modal: LegacyModal,
   module: 'ImageCrop' | 'ImageMarkers',
   isThisOpen: (clientOptions: unknown) => boolean
): { failed: Promise<never>; stop: () => void } {
   const event = `on${module}LoadError`;
   let onLoadError = (_clientOptions: unknown) => {};
   const failed = new Promise<never>((_resolve, reject) => {
      onLoadError = clientOptions => {
         if (isThisOpen(clientOptions)) {
            reject(new SentryError(`The ${module} dialog did not load.`));
         }
      };
   });
   Modal.addEvent(event, onLoadError);
   return { failed, stop: () => Modal.removeEvent(event, onLoadError) };
}

/**
 * A MediaItem for a bare image record. The dialogs replace most of its data
 * from their own server payloads, so renditions only need to be complete
 * enough to paint the initial canvas.
 */
async function createMediaItem(image: LegacyImageInput): Promise<LegacyMediaItem> {
   const [{ MediaItem }] = await Promise.all([
      import('Shared/FrameModules/MediaLibrary/item'),
      // Registers the 'GuideImage' constructor createFromData looks up.
      import('Shared/FrameModules/MediaLibrary/item_image'),
   ]);
   // The markers canvas sources getSrc('medium'), but API image data often
   // omits that rendition. The image server renders any size on demand, so
   // derive it from another size the way media_manager.js does — swapping
   // only a real extension, so a dotless URL passes through unchanged.
   const source = image.medium ?? image.standard ?? image.original ?? image.thumbnail;
   const medium = image.medium ?? (source ? source.replace(/\.[^./]+$/, '.medium') : null);
   return MediaItem.createFromData({
      imageid: image.imageid,
      type: 'GuideImage',
      thumbnail: image.thumbnail ?? null,
      standard: image.standard ?? null,
      medium,
      original: image.original ?? null,
   }) as LegacyMediaItem;
}

function editedImage(item: LegacyMediaItem): LegacyEditedImage | null {
   const rendition = (field: string) => {
      const value = item.data[field]?.();
      return typeof value === 'string' && value !== '' ? value : null;
   };
   const imageid = Number(item.data.imageid?.());
   if (!Number.isFinite(imageid)) {
      return null;
   }
   return {
      imageid,
      thumbnail: rendition('thumbnail'),
      standard: rendition('standard'),
      medium: rendition('medium'),
      original: rendition('original'),
   };
}

/**
 * Hold the pending-modal count from the call until the dialog leaves the
 * screen. Not until the promise settles: both dialogs keep resolving through
 * the background image job a save kicks off, which runs 10+ seconds with
 * nothing on screen and never finishes at all if the job stalls. Held that
 * long, the step rail goes on refusing switches with "Close the open dialog
 * before switching steps." over a dialog the author already closed (#64245).
 *
 * `dismissed` is what each dialog calls when it pops; the release here is the
 * backstop for one that throws or never opens.
 */
async function whileDialogOnScreen<T>(open: (dismissed: () => void) => Promise<T>): Promise<T> {
   const release = beginModalPending();
   try {
      return await open(release);
   } finally {
      release();
   }
}

/**
 * Open the markers dialog. Resolves with the marked-up replacement image
 * once its server write lands, or null when the user cancels (markers
 * always mint a new image; the original stays untouched).
 *
 * `onProcessingStart` fires when the dialog closes on a save whose write is
 * still in flight — the window between the user saving and the re-rendered
 * image being ready, which can run 10+ seconds. Callers use it to show a
 * spinner where the image will land; it never fires on cancel.
 *
 * A pending modal from the call until dismissal, load time included (#64245).
 */
export function openImageMarkers(
   image: LegacyImageInput,
   onProcessingStart?: () => void,
   stepBullets: StepBullet[] = []
): Promise<LegacyEditedImage | null> {
   return whileDialogOnScreen(dismissed =>
      showImageMarkers(image, onProcessingStart, stepBullets, dismissed)
   );
}

async function showImageMarkers(
   image: LegacyImageInput,
   onProcessingStart: (() => void) | undefined,
   stepBullets: StepBullet[],
   dismissed: () => void
): Promise<LegacyEditedImage | null> {
   const [Modal, mediaItem] = await Promise.all([loadModal(), createMediaItem(image)]);
   // Register the boot handlers only when nobody else has: pages that
   // bundle the legacy media_manager.js (any page with a media target)
   // already wire onImageMarkersLoad at DomReady, and a second handler
   // would re-run initialize against clientOptions the first run already
   // consumed (initialize deletes mediaImage), crashing the dialog.
   if (!Modal.$events?.imageMarkersLoad?.length) {
      // The dialog implementation arrives with the modal payload:
      // ImageMarkersFrameModule ships the image-markers entry, and the
      // modal's AssetGroup runs it (setting window.ImageMarkers) before
      // firing this Load event.
      Modal.addEvents({
         onImageMarkersLoad: (contentBox: unknown, options: unknown, responseData: unknown) => {
            markersDialog()?.initialize(contentBox, options, responseData);
         },
         onImageMarkersUnload: () => {
            markersDialog()?.unload();
         },
      });
   }
   const clientOptions = { mediaImage: mediaItem, stepBullets };
   const loadError = watchLoadError(Modal, 'ImageMarkers', options => options === clientOptions);
   const outcome = new Promise<LegacyEditedImage | null>(resolve => {
      Modal.open({
         type: 'module',
         name: 'ImageMarkers',
         boxClass: 'mediaLibraryModalBox editPhoto',
         clientOptions,
         serverOptions: { imageid: image.imageid },
         // Saving pops the dialog before its write (which mints a *new*
         // image) finishes; whenReady fires once the data lands — or
         // immediately when the user cancelled and no write started. Its
         // second callback is that same not-yet signal, which is what keeps
         // the spinner off the cancel path.
         onClose: () => {
            dismissed();
            mediaItem.whenReady(() => {
               const edited = editedImage(mediaItem);
               resolve(edited === null || edited.imageid === image.imageid ? null : edited);
            }, onProcessingStart);
         },
      });
   });
   try {
      return await Promise.race([loadError.failed, outcome]);
   } finally {
      loadError.stop();
   }
}

function markersDialog(): ImageMarkersDialog | undefined {
   return (window as { ImageMarkers?: ImageMarkersDialog }).ImageMarkers;
}

/**
 * Open the crop dialog. `accepts` is the MediaFilters collection the crop
 * validates against (e.g. 'guide_step_image'). Resolves with the cropped
 * replacement image once the crop job finishes, or null on cancel.
 *
 * `onProcessingStart` fires the moment the dialog closes and the background
 * crop job begins — the window between the user finishing and the cropped
 * image being ready. Callers use it to show a spinner where the image will
 * land; it never fires on cancel.
 *
 * A pending modal from the call until dismissal, as openImageMarkers is.
 */
export function openImageCrop(
   image: LegacyImageInput,
   accepts: string,
   onProcessingStart?: () => void
): Promise<LegacyEditedImage | null> {
   return whileDialogOnScreen(dismissed =>
      showImageCrop(image, accepts, onProcessingStart, dismissed)
   );
}

async function showImageCrop(
   image: LegacyImageInput,
   accepts: string,
   onProcessingStart: (() => void) | undefined,
   dismissed: () => void
): Promise<LegacyEditedImage | null> {
   const [{ initImageCropModule }, mediaItem, Modal] = await Promise.all([
      import('Shared/FrameModules/Image/image_crop'),
      createMediaItem(image),
      loadModal(),
   ]);
   // On legacy pages FrameModules.execute() runs this when the page declares
   // ImageCropFrameModule; this page has no legacy frame modules, so boot
   // the dialog (window.ImageCrop + its Modal events) directly.
   initImageCropModule();
   const dialog = (window as { ImageCrop?: ImageCropDialog }).ImageCrop;
   if (!dialog) {
      return null;
   }
   // showForMediaItem builds the clientOptions around the item made here.
   const loadError = watchLoadError(
      Modal,
      'ImageCrop',
      options => (options as { mediaItem?: unknown } | undefined)?.mediaItem === mediaItem
   );
   const outcome = new Promise<LegacyEditedImage | null>(resolve => {
      dialog.showForMediaItem(mediaItem, { getMediaFilterName: () => accepts }, cropped => {
         dismissed();
         if (!cropped) {
            resolve(null);
            return;
         }
         // The dialog closes when the crop *request* is accepted; the image
         // itself comes from a background job the item's data promise tracks.
         onProcessingStart?.();
         cropped.whenReady(() => resolve(editedImage(cropped)));
      });
   });
   try {
      return await Promise.race([loadError.failed, outcome]);
   } finally {
      loadError.stop();
   }
}

/** Kept structural so this module doesn't depend on the Media Manager's types. */
type LibraryImageItem = { imageid?: number } & LegacyImageRenditions;

/** openMediaManager's editItem contract; see open_media_manager.tsx. */
type MediaEditHandles = { closed: Promise<void>; saved: Promise<void> };

/**
 * openMediaManager's editItem, giving a library tile the Markers and Crop the
 * legacy media manager's menu has (item_menu.js). `stepBullets` is for a
 * manager opened from a guide step: Markers reads the step's lines when it
 * opens, as it does from the step's own image menu.
 */
export function editLibraryImage(
   accepts: string,
   stepBullets?: () => StepBullet[]
): (item: LibraryImageItem, action: 'markers' | 'crop') => MediaEditHandles {
   return (item, action) => {
      if (item.imageid === undefined) {
         return { closed: Promise.resolve(), saved: Promise.resolve() };
      }
      let resolveClosed!: () => void;
      let resolveSaved!: () => void;
      const closed = new Promise<void>(resolve => {
         resolveClosed = resolve;
      });
      const saved = new Promise<void>(resolve => {
         resolveSaved = resolve;
      });
      const image = {
         imageid: item.imageid,
         thumbnail: item.thumbnail ?? null,
         standard: item.standard ?? null,
         medium: item.medium ?? null,
         original: item.original ?? null,
      };
      const edited =
         action === 'markers'
            ? openImageMarkers(image, resolveClosed, stepBullets?.())
            : openImageCrop(image, accepts, resolveClosed);
      // onProcessingStart never fires on cancel or on a dialog that fails to
      // open, and a `closed` left pending keeps the manager shut for good.
      void edited
         .catch(() => {
            // The manager reopens either way, so without this the tile menu
            // just blinks and nothing happens.
            toast.error(_js('The image editor did not open. Try again.'), { duration: 5000 });
         })
         .then(() => {
            resolveClosed();
            resolveSaved();
         });
      return { closed, saved };
   };
}

/**
 * Duplicate an image into the current user's media library (the image
 * menu's "copy": edits to the copy can't touch other guides). Resolves
 * true when the server confirms the copy.
 */
export async function copyImageToMediaLibrary(imageid: number): Promise<boolean> {
   // copyToLibrary responds `false` (not an error) for a logged-out user.
   const result = await callAjaxIO<unknown>('copyToLibrary', imageid, 'GuideImage');
   return result !== false;
}

/**
 * Copy an image into the user's media library and report the outcome with a
 * toast — the shared action behind the step and primary-image "Copy to media
 * library" menu items.
 */
export async function copyImageWithToast(imageid: number): Promise<void> {
   try {
      if (await copyImageToMediaLibrary(imageid)) {
         toast.success(_js('Copied to your media library.'), { duration: 4000 });
         return;
      }
   } catch {
      // fall through to the shared error toast
   }
   toast.error(_js('The image did not copy. Try again.'), { duration: 5000 });
}
